import { Icon } from '@buggy/shared'
import { Button, Flex } from 'antd'
import { useState, type FunctionComponent, type ReactNode } from 'react'
import styled from 'styled-components'
import CollaborativeItemList from '@/components/common/collaborative/collaborative-item-list'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import AddScoringModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/add-scoring-modal'
import PostList from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/post-list'
import ScoringList from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/scoring-list'
import { NoData } from '@/components/kol/detail/no-data'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import type {
  CollaborationRecord,
  CollaborationRecordScoringType,
} from '@/types/schema/directory/collaboration-record'

const MAXIMUM_SCORING_COUNT = 20

interface CollaborationRecordListExpandedRowProps {
  record: CollaborationRecord
  kolId: string
  kolName: string
  collaborationRecordScoringType: CollaborationRecordScoringType
}

const CollaborationRecordListExpandedRow: FunctionComponent<
  CollaborationRecordListExpandedRowProps
> = ({ record, kolId, kolName, collaborationRecordScoringType }) => {
  const { formatMessage } = useIntl()

  const directoryActionPermission = useDirectoryActionPermission()

  const [addScoringModalOpen, setAddScoringModalOpen] = useState(false)

  const isScoringCountInsufficient =
    record.scoring_list.length >= MAXIMUM_SCORING_COUNT

  return (
    <Container>
      <Flex vertical gap={16}>
        <SectionTitle>
          {formatMessage({ id: 'campaign:cooperation_title' })}
        </SectionTitle>
        {record.item_list.length > 0 ? (
          <CollaborativeItemList items={record.item_list} />
        ) : (
          <NoData />
        )}
      </Flex>
      <Flex vertical gap={16}>
        <Flex justify='space-between'>
          <SectionTitle>
            {formatMessage({
              id: 'kol_cooperation_detail_drawer:tabs_cooperation_history',
            })}
          </SectionTitle>
          <PermissionInsufficientTooltip
            insufficient={!directoryActionPermission}
            title={(): ReactNode => {
              if (isScoringCountInsufficient) {
                return formatMessage(
                  {
                    id: 'kol_cooperation_detail_drawer:add_scoring_reach_maximum_count',
                  },
                  {
                    count: MAXIMUM_SCORING_COUNT,
                  },
                )
              }
            }}
          >
            <AddScoringButton
              ghost
              disabled={
                !directoryActionPermission || isScoringCountInsufficient
              }
              icon={<Icon name='plus' />}
              size='small'
              type='link'
              onClick={() => setAddScoringModalOpen(true)}
            >
              {formatMessage({ id: 'autoreport:chart_field_add_btn' })}
            </AddScoringButton>
          </PermissionInsufficientTooltip>
        </Flex>
        {record.scoring_list.length > 0 ? (
          <ScoringList
            collaborationRecordId={record.id}
            collaborationRecordName={record.name}
            collaborationRecordScoringType={collaborationRecordScoringType}
            data={record.scoring_list}
            kolId={kolId}
            kolName={kolName}
          />
        ) : (
          <NoData />
        )}
      </Flex>
      <Flex vertical gap={16}>
        <SectionTitle>
          {formatMessage({ id: 'general:column_title_posts' })}
        </SectionTitle>
        {record.post_list.length > 0 ? (
          <PostList
            collaborationRecordScoringType={collaborationRecordScoringType}
            kolId={kolId}
            posts={record.post_list}
          />
        ) : (
          <NoData />
        )}
      </Flex>
      <AddScoringModal
        collaborationRecordId={record.id}
        collaborationRecordName={record.name}
        collaborationRecordScoringType={collaborationRecordScoringType}
        kolId={kolId}
        kolName={kolName}
        open={addScoringModalOpen}
        onClose={() => setAddScoringModalOpen(false)}
      />
    </Container>
  )
}

const Container = styled.div`
  margin: 0 8px 16px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 24px;
  background: ${({ theme }): string => theme.colors.background.grey};

  .ant-empty {
    margin: 0;
    padding: 30px 0;
    background: ${({ theme }): string => theme.colors.background.white};
  }
`

const SectionTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
`

const AddScoringButton = styled(Button)`
  --ant-margin-xs: 4px;

  padding: 0;
  height: 24px;
  line-height: 24px;
`

export default CollaborationRecordListExpandedRow
